import { Fragment, useEffect, useMemo, useState } from 'react';
import DataTable from 'react-data-table-component';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { editarBreadcrumb } from '../../../actions/breadcrumb';
import { buscarPaquetes, limpiarPaquetes } from '../../../actions/configuracion';
import { formatNumber } from '../../../utils/generalFunctions';

import EliminarPaqueteModal from './EliminarPaqueteModal';

const formatMoney = (n) => formatNumber(Number(n || 0), 2, '$', '');

const Paquetes = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { listado } = useSelector(state => state.configuracion?.paquetes || { listado: [] });

    const [textoFiltro, setTextoFiltro] = useState('');
    const [paqueteEliminar, setPaqueteEliminar] = useState(null);

    useEffect(() => {
        dispatch(editarBreadcrumb([
            { activo: true, nombre: 'Paquetes', path: '/configuracion/paquetes' }
        ]));
    }, [dispatch]);

    useEffect(() => {
        dispatch(buscarPaquetes());

        return () => {
            dispatch(limpiarPaquetes());
        };
    }, [dispatch]);

    const dataFiltrada = useMemo(() => {
        const q = (textoFiltro || '').trim().toLowerCase();
        if (!q) return listado || [];

        return (listado || []).filter((p) => {
            const nombre = (p?.nombre || '').toLowerCase();
            const codigo = (p?.codigo || '').toLowerCase();
            const descripcion = (p?.descripcion || '').toLowerCase();
            return nombre.includes(q) || codigo.includes(q) || descripcion.includes(q);
        });
    }, [listado, textoFiltro]);

    const columns = useMemo(() => ([
        {
            name: 'Código',
            selector: row => row?.codigo || 'Sin definir',
            sortable: true,
            wrap: true,
            grow: 1
        },
        {
            name: 'Nombre',
            selector: row => row?.nombre || 'Sin definir',
            sortable: true,
            wrap: true,
            grow: 2
        },
        {
            name: 'Precio',
            selector: row => row?.precio || 0,
            sortable: true,
            right: true,
            cell: row => formatMoney(row?.precio),
            grow: 1
        },
        {
            name: 'IVA',
            selector: row => (row?.iva ? 1 : 0),
            sortable: true,
            center: true,
            cell: row => (row?.iva ? 'Sí' : 'No'),
        },
        {
            name: 'Total productos',
            selector: row => (row?.total_productos ?? -1),
            sortable: true,
            center: true,
            cell: row => (Number.isFinite(row?.total_productos) ? row.total_productos : '—'),
        },
        {
            name: 'Opciones',
            cell: row => (
                <div className="d-flex align-items-center gap-2">
                    <button
                        type="button"
                        className="btn btn-sm btn-link px-2"
                        title="Editar"
                        onClick={() => navigate(`/configuracion/paquetes/editar/${row._id}`)}
                    >
                        <i className="fa fa-edit text-warning" />
                    </button>

                    <button
                        type="button"
                        className="btn btn-sm btn-link px-2"
                        title="Eliminar"
                        onClick={() => setPaqueteEliminar(row)}
                    >
                        <i className="fas fa-trash text-danger" />
                    </button>
                </div>
            )
        }
    ]), [navigate]);

    const paginationOptions = {
        rowsPerPageText: 'Filas por página',
        rangeSeparatorText: 'de',
        selectAllRowsItem: true,
        selectAllRowsItemText: 'Todos'
    };

    return (
        <Fragment>
            <div className="row">
                <div className="col-md-10 offset-md-1 pb-4">
                    <h3 className="my-5">Paquetes</h3>

                    <div className="d-flex justify-content-end mb-3">
                        <div className="d-flex align-items-center gap-2 w-100 justify-content-end">
                            <div style={{ minWidth: 280 }}>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Buscar en la tabla..."
                                    value={textoFiltro}
                                    onChange={(e) => setTextoFiltro(e.target.value)}
                                />
                            </div>

                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={() => navigate('/configuracion/paquetes/nuevo')}
                            >
                                <i className="feather icon-plus" /> Nuevo paquete
                            </button>
                        </div>
                    </div>

                    <div className="mt-2">
                        <DataTable
                            columns={columns}
                            data={dataFiltrada || []}
                            pagination
                            paginationComponentOptions={paginationOptions}
                            highlightOnHover
                            striped
                            responsive
                            persistTableHead
                            noDataComponent="Sin paquetes para mostrar"
                        />
                    </div>
                </div>
            </div>

            <EliminarPaqueteModal
                show={!!paqueteEliminar}
                paquete={paqueteEliminar}
                onHide={() => setPaqueteEliminar(null)}
                onSuccess={async () => {
                    setPaqueteEliminar(null);
                }}
            />
        </Fragment>
    );
};

export default Paquetes;
